/**
 * Digital Product Helpers
 *
 * Utilities for handling digital products (e-learning, digital tools) in the cart.
 * Digital products have special checkout behavior:
 * - Only one item allowed in cart at a time
 * - Direct redirect to checkout v2
 * - Cart cleared when switching between different digital products
 */

import { type History } from 'history';
import { List, Map } from 'immutable';
import type { Dispatch as ReduxDispatch } from 'redux';
import {
  commerceCartRemoveProduct,
} from '@uncinc/react-drupal-core/dist/redux/actions/Commerce';
import {
  PRODUCT_VARIATION_DIGITAL_TOOL,
  PRODUCT_VARIATION_ELEARNING,
} from '../../../../config/constants';
import { ROUTE_CHECKOUT_V2, ROUTE_USER_CHECK } from '../../../../config/routes';

type Dispatch = ReduxDispatch<any>;

// =============================================================================
// Constants
// =============================================================================

/**
 * Product types that are considered digital products.
 * These require the specialized checkout flow (single item cart, direct checkout).
 * Values match the product entity's type.target_id field.
 */
const DIGITAL_PRODUCT_TYPES = [
  'e_learning',
  'digital_tool',
] as const;

/**
 * Product variation type strings as they appear in JSON:API cart responses.
 * Used for identifying digital products in the cart.
 */
const DIGITAL_PRODUCT_VARIATIONS = [
  'e-learning',
  'digital-tool',
] as const;

// =============================================================================
// Product Type Checks
// =============================================================================

/**
 * Checks if a product entity is a digital product (e-learning or digital tool).
 * Digital products require the specialized checkout flow with single-item cart.
 *
 * @param productEntity - ImmutableJS product entity
 * @returns True if the product is a digital product type
 */
export function isDigitalProduct(productEntity: Map<string, any>): boolean {
  const productType = productEntity.getIn(['type', 0, 'target_id'], '') as string;
  return (DIGITAL_PRODUCT_TYPES as readonly string[]).includes(productType);
}

/**
 * Checks if a cart product type string matches a digital product variation.
 * Used internally to identify digital products within cart items.
 *
 * @param productType - Product type string from cart item's purchased_entity
 * @returns True if it matches a digital product variation
 */
function isDigitalProductVariation(productType: string): boolean {
  return DIGITAL_PRODUCT_VARIATIONS.some((variation) => productType.includes(variation));
}

// =============================================================================
// Cart Inspection Functions
// =============================================================================

/**
 * Checks if cart contains any digital products (e-learning or digital tools).
 * Used to determine if the specialized checkout flow should be used.
 *
 * @param cartProducts - ImmutableJS List of cart products
 * @returns True if cart contains at least one digital product
 */
export function cartHasDigitalProduct(cartProducts: List<any> | undefined): boolean {
  if (!cartProducts || cartProducts.size === 0) return false;

  return cartProducts.some((product) => {
    const productType = product.getIn(['relationships', 'purchased_entity', 'type'], '');
    return isDigitalProductVariation(productType);
  });
}

/**
 * Checks if cart contains specifically e-learning products (not digital tools).
 * Used for features that only apply to e-learning, such as "order for someone else".
 *
 * @param cartProducts - ImmutableJS List of cart products
 * @returns True if cart contains e-learning products specifically
 */
export function cartHasElearningProduct(cartProducts: List<any> | undefined): boolean {
  if (!cartProducts || cartProducts.size === 0) return false;

  return cartProducts.some((product) => {
    const productType = product.getIn(['relationships', 'purchased_entity', 'type'], '');
    return productType.includes(PRODUCT_VARIATION_ELEARNING);
  });
}

/**
 * Checks if the same digital product (by SKU) already exists in cart.
 * Used to prevent duplicate digital product purchases.
 *
 * @param cartProducts - ImmutableJS List of cart products
 * @param productSku - SKU of the product being added
 * @returns True if the same digital product is already in cart
 */
export function cartHasSameDigitalProduct(
  cartProducts: List<any> | undefined,
  productSku: string,
): boolean {
  if (!cartProducts || cartProducts.size === 0) return false;
  if (!productSku) return false;

  return cartProducts.some((product) => {
    const cartProductSku = product.getIn(
      ['relationships', 'purchased_entity', 'attributes', 'sku'],
      '',
    );
    const productType = product.getIn(['relationships', 'purchased_entity', 'type'], '');
    return cartProductSku === productSku && isDigitalProductVariation(productType);
  });
}

// =============================================================================
// Cart Operations
// =============================================================================

/**
 * Clears all products from cart and waits for completion.
 * Used when switching between incompatible product types.
 *
 * @param dispatch - Redux dispatch function
 * @param cartProducts - ImmutableJS List of cart products to remove
 *
 * @remarks
 * TODO: The setTimeout(300) is a workaround because commerceCartRemoveProduct
 * doesn't return a Promise we can await. Proper solutions:
 * 1. Update @uncinc/react-drupal-core to return Promise from thunk actions
 * 2. Watch Redux state (ui.commerce.operations.cart_product_remove.status) for SUCCESS
 * 3. Add backend "clear cart" endpoint to remove all items in one request
 */
export async function clearCart(
  dispatch: Dispatch,
  cartProducts: List<any> | undefined,
): Promise<void> {
  if (!cartProducts || cartProducts.size === 0) return;

  // Remove products sequentially to avoid race conditions
  await cartProducts.reduce(
    (promise, product) => promise.then(() => {
      const productId = product.get('id');
      dispatch(commerceCartRemoveProduct(productId));
      return new Promise((resolve) => { setTimeout(resolve, 300); });
    }),
    Promise.resolve(),
  );
}

// =============================================================================
// Navigation
// =============================================================================

/**
 * Redirects user to appropriate checkout route based on login status.
 * Logged-in users go directly to checkout, others to the login/check page.
 *
 * @param loggedIn - Whether user is currently logged in
 * @param history - React Router history object for navigation
 */
export function redirectToCheckout(loggedIn: boolean, history: History): void {
  const redirectPath = loggedIn ? ROUTE_CHECKOUT_V2 : ROUTE_USER_CHECK;
  history.push(redirectPath);
}

// =============================================================================
// Product Type Detection
// =============================================================================

/**
 * Product types supported in checkout.
 * Used for dynamic page titles and product-specific checkout behavior.
 */
export type ProductType = 'e_learning' | 'digital_tool' | 'physical' | 'unknown';

/**
 * Determines the product type from cart products.
 * Used for checkout page title and conditional feature rendering.
 *
 * @param cartProducts - ImmutableJS List of cart products
 * @returns Product type identifier based on cart contents
 */
export function getProductType(cartProducts: List<any> | undefined): ProductType {
  if (!cartProducts || cartProducts.size === 0) return 'unknown';

  const firstProduct = cartProducts.get(0);
  const productType = firstProduct?.getIn(
    ['relationships', 'purchased_entity', 'type'],
    '',
  ) || '';

  if (productType.includes(PRODUCT_VARIATION_ELEARNING)) {
    return 'e_learning';
  }

  if (productType.includes(PRODUCT_VARIATION_DIGITAL_TOOL)) {
    return 'digital_tool';
  }

  return 'physical';
}
